import classNames from 'classnames';
import * as React from 'react';
import { useIntl } from 'react-intl';
import Linkify from 'react-linkify';
import * as IInputs from '../../../../../interfaces/merchants/resources/books/inputs';
import StringUtils from '../../../../../components/utils/StringUtils';
import BookingWithPageDetailImage from '../../../../../molecules/merchant/resources/book/BookingWithPageDetailImage';

import usePerformableStaffMembers from '../../../../../hooks/api/usePerformableStaffMembers';

import css from './BookingWithPageDetail.module.scss';
import Heading from '../../../../../atoms/Heading';
import MerchantCourseDetailsStaffList from '../../../../../molecules/merchant/resources/book/MerchantCourseDetailsStaffList';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import dynamic from 'next/dynamic';

const RichViewer = dynamic(
  () => import('../../../../../components/RichViewer'),
  {
    ssr: false,
  },
);
interface ElementProps {
  merchantResource: {
    name: string;
    publicId: string;
    coverContentsType: string;
    primaryImage?: {
      meta: {
        width: number;
        height: number;
      };
      thumb?: {
        url: string;
      };
    };
    coverContent?: {
      url: string;
      embed: string;
      thumbnailUrl: string;
    };
    isJsonDescription: boolean;
    description?: string;
    descriptionText?: string;
  };
  merchantId: string;

  // 担当スタッフのコンポーネントを出すかどうか。 M4ではスタッフ/メニュー選択タブがあるので出す必要がない。詳しくはこの画像を見てください https://coubic.gyazo.com/cb87f04d187ecbfbbfc30c7815c12a22
  showPerformableStaffMembers: boolean;
  children: React.ReactNode;
}

type Props = ElementProps;

const initialShowBookingContentBy = (inputs: IInputs.State): boolean =>
  inputs.reservationId ? false : true;

const BookingWithPageDetail: React.FC<Props> = (props) => {
  const inputReduxState = useShallowEqualSelector(
    (state: ReduxState) => state.merchantsResourcesBooksInputs,
  );

  const [showPageDetail, setShowPageDetail] = React.useState(
    initialShowBookingContentBy(inputReduxState),
  );
  const showHideButtonClasses = classNames(
    css.BookingWithPageDetail__ShowHideButton,
    { [css.isHide]: !showPageDetail },
  );
  const showHideContentClasses = classNames(
    css.BookingWithPageDetail__ShowHideContents,
    { [css.isHide]: !showPageDetail },
  );
  const intl = useIntl();

  const { showPerformableStaffMembers, merchantId, merchantResource } = props;

  const { data } = usePerformableStaffMembers(
    showPerformableStaffMembers && merchantId && merchantResource.publicId
      ? {
          merchantPublicId: merchantId,
          bookingPageId: merchantResource.publicId,
        }
      : undefined, // do not request
  );
  const performableStaffMembers = data?.data; // dataのdataとかややこしい...

  return (
    <div className={classNames(css.BookingWithPageDetail)}>
      <div className={css.BookingWithPageDetail__PageDetail}>
        <div className={css.BookingWithPageDetail__Heading}>
          <div className={css.BookingWithPageDetail__Title}>
            {merchantResource.name}
          </div>
          <button
            className={showHideButtonClasses}
            onClick={(ev) => {
              ev.preventDefault();
              setShowPageDetail(!showPageDetail);
            }}
          >
            {intl.formatMessage({
              id: 'books.bookingWithPageDetail.detail',
            })}
          </button>
        </div>
        <div className={showHideContentClasses}>
          <div className={css.BookingWithPageDetail__Thumbnail}>
            {merchantResource.coverContentsType === 'upload_image' &&
              merchantResource.primaryImage && (
                <BookingWithPageDetailImage
                  name={merchantResource.name}
                  primaryImage={merchantResource.primaryImage}
                />
              )}
            {merchantResource.coverContentsType === 'embed' &&
              merchantResource.coverContent &&
              merchantResource.coverContent.embed && (
                <div
                  className={css.BookingWithPageDetail__Embed}
                  dangerouslySetInnerHTML={{
                    __html: merchantResource.coverContent.embed,
                  }}
                />
              )}
          </div>
          <div className={css.BookingWithPageDetail__Description}>
            {merchantResource.isJsonDescription &&
            merchantResource.description ? (
              <RichViewer value={merchantResource.description} />
            ) : (
              merchantResource.descriptionText && (
                <Linkify>
                  {StringUtils.simpleFormat(merchantResource.descriptionText)}
                </Linkify>
              )
            )}
          </div>

          {performableStaffMembers && performableStaffMembers.length > 0 && (
            <div className={css.staffMembersWrap}>
              <div className={css.staffMemberSectionHeading}>
                <Heading>
                  {intl.formatMessage({
                    id: 'books.formsLabel.staff',
                  })}
                </Heading>
              </div>
              <MerchantCourseDetailsStaffList staff={performableStaffMembers} />
            </div>
          )}
        </div>
        <div className={css.BookingWithPageDetail__Clearfix} />
      </div>

      <div className={css.BookingWithPageDetail__BookingContents}>
        {props.children}
      </div>
    </div>
  );
};

export default BookingWithPageDetail;
